import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { Dispatch, SetStateAction } from 'react';
import { useForm } from 'react-hook-form';
import { useSetCustomUserProfile, useUserProfile } from '~/services/shop/hooks';
import { useTranslations } from '~/services/umbraco/hooks';
import { Button } from '~/shared/components/Button';
import { ErrorMessage, Form, Input as CustomInput } from '~/shared/components/Form';
import { Input } from '~/shared/components/Form/components/styled';
import { Modal } from '~/shared/components/Modal';
import { Text } from '~/shared/components/Text';
import { Validation } from '~/shared/utils/validation';
import { theme } from '~/theme';

type Props = {
    setShowOverlay: Dispatch<SetStateAction<boolean>>;
    showOverlay: boolean;
};

type FormFields = {
    [Fields.Email]: string;
    [Fields.Name]: string;
    [Fields.PhoneNumber]: string;
};

enum Fields {
    Email = 'email',
    Name = 'name',
    PhoneNumber = 'phoneNumber',
}

export default function UserProfileModal({ setShowOverlay, showOverlay }: Props) {
    const { translate } = useTranslations();
    const { mutate: setNewProfile, isLoading, error } = useSetCustomUserProfile();
    const { data } = useUserProfile();
    const user = data?.user;
    const methods = useForm<FormFields>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            [Fields.Email]: user?.email,
            [Fields.Name]: user?.name,
            [Fields.PhoneNumber]: user?.phoneNumber,
        },
    });
    const { getValues, formState, reset, handleSubmit } = methods;

    const hasChanges =
        user?.name !== getValues()[Fields.Name] ||
        user?.phoneNumber !== getValues()[Fields.PhoneNumber];

    function onSubmit(formData: FormFields) {
        if (!error && hasChanges) {
            setNewProfile(formData);
            setShowOverlay(false);
        }
        setShowOverlay(false);
    }

    function onCancel() {
        reset({
            [Fields.Name]: user?.name,
            [Fields.PhoneNumber]: user?.phoneNumber,
        });
        setShowOverlay(false);
    }

    return (
        <Modal handleClose={onCancel} isOpen={showOverlay}>
            <Container>
                <Text variant="display2">
                    {translate('mit-fog.userProfile.userInformation.editUser')}
                </Text>

                <Form methods={methods} onSubmit={onSubmit}>
                    <EmailInputField
                        disabled
                        hideStar
                        type="email"
                        inputMode="email"
                        name={Fields.Email}
                        placeholder={translate('mit-fog.userProfile.email')}
                        required
                        valueAsNumber={false}
                    />

                    <CustomInput
                        hideStar
                        type="text"
                        inputMode="text"
                        name={Fields.Name}
                        pattern={{
                            value: Validation.Name,
                            message: translate('mit-fog.userProfile.name.error'),
                        }}
                        placeholder={translate('mit-fog.userProfile.name')}
                        required={translate('mit-fog.userProfile.name.error')}
                        valueAsNumber={false}
                    />

                    <CustomInput
                        type="number"
                        inputMode="numeric"
                        name={Fields.PhoneNumber}
                        pattern={{
                            value: Validation.PhoneNumber,
                            message: translate('mit-fog.userProfile.phone.error'),
                        }}
                        hideStar
                        placeholder={translate('mit-fog.userProfile.phone')}
                        required={translate('mit-fog.userProfile.phone.error')}
                        valueAsNumber={false}
                    />

                    {error && (
                        <ErrorMessage>
                            {translate('checkout.information.submit.error')}
                        </ErrorMessage>
                    )}
                </Form>
            </Container>

            <BottomWrapper>
                <Button
                    onClick={handleSubmit(onSubmit)}
                    isLoading={isLoading}
                    disabled={!Boolean(formState.isValid)}
                    noIcon={!formState.isValid}
                >
                    {translate('mit-fog.userProfile.userInformation.saveChanges')}
                </Button>

                <Button onClick={onCancel} noIcon variant="stroke" color={theme.colors.nightBlack}>
                    {translate('mit-fog.userProfile.userInformation.cancel')}
                </Button>
            </BottomWrapper>
        </Modal>
    );
}

const Container = styled(motion.div)(({ theme }) => ({
    display: 'grid',
    gap: theme.space[4],
    maxHeight: '100%',
    height: '100%',
    gridTemplateRows: `${theme.space[6]} ${theme.space[6]} auto ${theme.space[6]}`,
    overflowY: 'scroll',
    '::-webkit-scrollbar': {
        display: 'none',
    },
}));

const EmailInputField = styled(CustomInput)(({ theme }) => ({
    border: `1px solid ${theme.colors.grey60}`,
    [`${Input}`]: {
        color: `${theme.colors.grey60}`,
    },
}));

const BottomWrapper = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    marginTop: 'auto',
    marginBottom: theme.space[4],
    gap: theme.space[2],
}));
